import { ReservationSale } from "../types/reservation-sale.interface";
import { ReservationAddress } from "./../types/reservation-address.interface";
import {
  CartItem,
  Discount,
  FoodPickupPointDeliveryData,
  FoodPickupPointPromise,
  InvoiceData,
  Product,
  ProductCategory,
  ReservationLocation,
  WoltDeliveryData,
  WoltDeliveryPromise,
} from "@/types";
import { AxiosInstance } from "axios";
import { ReservationPaymentEnum } from "../features/reservation/reservation-payment.enum";
import { DeepReadonly } from "vue";

export interface ReservationCheckoutResponse {
  paymentUrl?: string;
  /** Embedded BLIK: no order exists yet, the code is collected next. */
  embeddedBlik?: boolean;
  successUrl: string;
  reservationId: number;
}

export class ReservationApi {
  constructor(private axios: AxiosInstance) {}

  getProductCategories(
    address: ReservationAddress,
    sale: ReservationSale
  ): Promise<ProductCategory[]> {
    const isCategoryResponse = (
      response: ProductCategory[] | Product[]
    ): response is ProductCategory[] => {
      return response[0] && "category" in response[0];
    };

    return this.axios
      .get<ProductCategory[] | Product[]>("/products", {
        params: {
          sale_id: sale.id,
          token: sale.token,
        },
      })
      .then((r) => r.data)
      .then((response) => {
        return isCategoryResponse(response)
          ? response
          : [
              {
                products: response,
              },
            ];
      });
  }

  async getAdditionalProductsForCheckout(
    sale: ReservationSale
  ): Promise<Product[]> {
    const r = await this.axios.get<Product[]>("/additional-checkout-products", {
      params: {
        sale_id: sale.id,
        token: sale.token,
      },
    });
    return r.data;
  }

  checkout(
    cartItems: DeepReadonly<CartItem[]>,
    discounts: DeepReadonly<Discount[]>,
    sale: DeepReadonly<ReservationSale>,
    payment: ReservationPaymentEnum,
    deliveryPromise: WoltDeliveryPromise | FoodPickupPointPromise | null,
    deliveryData: WoltDeliveryData | FoodPickupPointDeliveryData | null,
    promisedGifts: { tier_id: number; product_id: number }[] = [],
    invoice: InvoiceData | null = null,
    embeddedBlik = false
  ): Promise<ReservationCheckoutResponse> {
    return this.axios
      .post<ReservationCheckoutResponse>(
        "/reservation/checkout",
        {
          discount: discounts,
          sale_id: sale?.id,
          token: sale?.token,
          payment: payment,
          blik_embedded: embeddedBlik,
          cart: cartItems.map((i) => ({
            productId: i.product.id,
            quantity: i.quantity,
          })),
          promised_gifts: promisedGifts,
          invoice: invoice ?? undefined,
          delivery:
            deliveryPromise && deliveryData
              ? {
                  ...deliveryData,
                  promise: deliveryPromise,
                }
              : undefined,
        }
      )
      .then((r) => r.data);
  }

  /**
   * Embedded BLIK: hand the code to our backend, which creates the Montonio order and returns
   * the payment intent the SDK waits on. The code is never stored on our side.
   */
  createBlikIntent(
    reservationId: number,
    blikCode: string
  ): Promise<{ paymentIntentUuid: string }> {
    return this.axios
      .post<{ paymentIntentUuid: string }>(
        `/reservation/${reservationId}/blik/intent`,
        { blik_code: blikCode }
      )
      .then((r) => r.data);
  }

  /** A fresh order to pay for on Montonio's own page, when the embedded flow cannot finish. */
  getBlikFallbackUrl(reservationId: number): Promise<{ paymentUrl: string }> {
    return this.axios
      .post<{ paymentUrl: string }>(
        `/reservation/${reservationId}/blik/fallback-url`
      )
      .then((r) => r.data);
  }

  getLastInvoice(): Promise<InvoiceData | null> {
    return this.axios
      .get<InvoiceData | null>("/reservation/last-invoice")
      .then((r) => r.data);
  }

  getLocations(
    params?: { sale_id: number; token: string } | { group_token: string }
  ): Promise<ReservationLocation[]> {
    return this.axios
      .get<ReservationLocation[]>("/locations", {
        params,
      })
      .then((r) => r.data);
  }

  getClosedLocations(): Promise<ReservationLocation[]> {
    return this.axios
      .get<ReservationLocation[]>("/locations/closed")
      .then((r) => r.data);
  }
}
